import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ScanSearch, ShieldCheck, Zap, FileSearch } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AtsChecker } from "@/components/AtsChecker";
import usePageMeta from "@/lib/usePageMeta";

const H2 = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mt-16 mb-6">
    {children}
  </h2>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-[15px] text-zinc-200 leading-relaxed">
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30 mt-0.5">
      <Check className="h-3 w-3 text-emerald-400" />
    </span>
    <span>{children}</span>
  </li>
);

const faqs = [
  {
    q: "Is the ATS checker really free?",
    a: "Yes — no account, no card, no catch. Upload your CV and get an honest 0-100 score with the three biggest issues, in seconds. We offer it free because most people who see their score want it fixed — that's our paid service.",
  },
  {
    q: "What does the score actually measure?",
    a: "Recruiter impact and ATS compatibility: structure, keyword coverage, action-verb strength, formatting risks, and clarity. The same GPT-5.2 engine that powers our paid rewrites does the analysis.",
  },
  {
    q: "Do you store my CV?",
    a: "No. The free check analyzes your file in memory, returns the score, and discards the content. Only a anonymized score record is kept for rate limiting.",
  },
  {
    q: "My score is low. Now what?",
    a: "That's exactly what CV AI PRO fixes: upload the same CV after checkout and the AI rewrites it completely — structure, keywords, impact bullets — and hands you a new PDF and DOCX in minutes.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function FreeAtsChecker() {
  usePageMeta({
    title: "Free ATS Resume Checker — Score Your CV in Seconds | CV AI PRO",
    description:
      "Free ATS resume checker: upload your CV and get an instant 0-100 score plus the 3 biggest issues killing your applications. No account needed. Fix it with AI in minutes.",
    path: "/free-ats-checker",
  });

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="free-ats-checker-page">
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <Navbar />

      <main className="relative pt-28 sm:pt-32 pb-10">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Free tool · No account needed</p>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white text-balance"
            data-testid="ats-page-h1"
          >
            Free ATS Resume Checker
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-balance">
            Applicant Tracking Systems reject 75% of resumes before a human reads them.
            Find out if yours survives — brutally honest score in seconds.
          </p>
        </div>

        {/* The checker itself */}
        <AtsChecker hideHeader />

        {/* SEO content */}
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <H2>How the ATS score works</H2>
          <p className="text-[15px] text-zinc-300 leading-relaxed">
            ATS software (Workday, Greenhouse, Lever, Taleo) parses your CV into structured data
            and ranks it against the job's keywords before any recruiter sees it. Our checker
            simulates that pipeline with the same AI engine behind our paid rewrites:
          </p>
          <ul className="mt-6 space-y-3">
            <Bullet>Structure — can the parser find your sections, dates, and titles?</Bullet>
            <Bullet>Keywords — do you mirror the language of the roles you want?</Bullet>
            <Bullet>Impact — action verbs and outcomes, or passive filler?</Bullet>
            <Bullet>Formatting risk — tables, graphics, and fonts that break parsers.</Bullet>
          </ul>

          <H2>What a good score looks like</H2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { Icon: Zap, range: "70–100", label: "Interview-ready", desc: "Strong structure and keyword coverage. Polish and send." },
              { Icon: FileSearch, range: "45–69", label: "Risky", desc: "Getting through sometimes, losing to better-formatted competitors." },
              { Icon: ShieldCheck, range: "0–44", label: "Filtered out", desc: "Very likely auto-rejected. This is fixable — in minutes." },
            ].map((c) => (
              <div key={c.range} className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5" data-testid={`ats-range-${c.range}`}>
                <c.Icon className="h-4 w-4 text-zinc-500" />
                <p className="mt-3 text-xl font-semibold tracking-tight text-white">{c.range}</p>
                <p className="text-sm text-zinc-300 mt-0.5">{c.label}</p>
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <H2>Frequently asked questions</H2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5">
                <p className="text-sm font-medium text-white">{f.q}</p>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8 text-center">
            <ScanSearch className="mx-auto h-6 w-6 text-zinc-400" />
            <p className="mt-4 text-xl sm:text-2xl font-semibold tracking-tight text-white text-balance">
              Score low? Let the AI fix it — not just flag it.
            </p>
            <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto text-balance">
              The same engine rewrites your entire CV: structure, keywords, impact bullets,
              delivered as PDF + DOCX in minutes. One-time payment, from $12.13.
            </p>
            <Link
              to="/#pricing"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="ats-page-cta"
            >
              Fix my CV with AI
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
